'use strict'

const ActionTypes = function () {}

ActionTypes.prototype.extension = {
    /**
     * Creates an action to add a proctor chat message into the floating menu chat
     * @param {{twilioMessage: string, author: string, timestamp: number}} chatMessage - Message object from Twilio Conversation
     * @returns {{executor: string, action: string, message, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    addConversationMessage: (chatMessage) => {
        return {
            action: 'addConversationMessage',
            chatMessage,
            executor: 'extension',
            type: 'action'
        };
    },
    /**
     * Creates an action object to raise or dismiss proctor voice chat alerts
     * @param {"voiceChatInitializing" | "voiceChatStarted" | "voiceChatEnded"} alertTitle - Name of the alert to raise or dismiss
     * @returns {{executor: string, action: string, type: string, alertTitle}} - Object to send in `addAction()` or `addActions()`
     */
    alertForProctorVoiceChat: (alertTitle) => {
        return {
            action: 'alertForProctorVoiceChat',
            alertTitle,
            executor: 'extension',
            type: 'action'
        }
    },
    /**
     * Creates an action object to apply a modality option to the current session
     * @param {Object[]} modalityOptions - Array of ModalityOption objects
     * @returns {{modalityOptions, executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    applyModalityOptions: (modalityOptions) => {
        return {
            action: 'applyModalityOptions',
            modalityOptions,
            executor: 'extension',
            type: 'action'
        };
    },
    /**
     * Creates an acton object to change the extension's icon color
     * @param {"gray" | "red" | "green" | "blue"} [color]
     * @returns {{color, executor: string, action: string, type: string}}
     */
    changeIconColor: (color = "gray") => {
        return {
            action: 'changeIconColor',
            color,
            executor: 'extension',
            type: 'action'
        };
    },
    /**
     * Creates an action object to alert the floating menu that the twilio conversation changed availability
     * @param {boolean} available - Whether or not the conversation is available and the button should be visible in the floating menu
     * @returns {{executor: string, available, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    conversationAvailabilityChange: (available) => {
        return {
            action: 'conversationAvailabilityChange',
            available,
            executor: 'extension',
            type: 'action'
        };
    },
    /**
     * Creates an action object for the extension to end the session
     * @param {"None" | "ChromeStoppedSharing" | "UnstableInternet"} [endPopup] - If a session is ending atypically, we want to show a popup, so this would represent which popup to show
     * @param {string | null} [sessionEndRedirectUrl] - Full URL to redirect student to after their session
     * @returns {{requiredSyncDocField: string, executor: string, action: string, type: string, normalEnd: boolean}} - Object to send in `addAction()` or `addActions()`
     */
    endSession: (endPopup = "None", sessionEndRedirectUrl = null) => {
        const returnObject = {
            action: 'endSession',
            executor: 'extension',
            endPopup,
            requiredSyncDocField: 'syncDocCreationDate',
            type: 'action'
        };
        if (sessionEndRedirectUrl) {
            returnObject.sessionEndRedirectUrl = sessionEndRedirectUrl;
        }
        return returnObject;
    },
    /**
     * Creates an action object for enabling all needed permissions for a session
     * @param {string} [examUrl] - Exam redirect URL
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    enablePermissions: (examUrl = null) => {
        const returnObject = {
            action: 'enablePermissions',
            executor: 'extension',
            type: 'action'
        };
        if (examUrl) {
            returnObject.examUrl = examUrl;
        }
        return returnObject;
    },
    /**
     * Creates an action object to list the permissions for the Onboarding and Session UIs as well as optionally the Exam Url
     * @param {string} [examUrl] - Exam Redirect URL
     * @param {'onboarding'|'session'|null} [respondTo] - Name of the action array to put the response object
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    getAllowedPermissions: (examUrl = null, respondTo = null) => {
        const returnObject = {
            action: 'getAllowedPermissions',
            executor: 'extension',
            type: 'action'
        };
        if (respondTo) {
            returnObject.respondTo = respondTo;
        }
        if (examUrl) {
            returnObject.examUrl = examUrl;
        }
        return returnObject;
    },
    /**
     * Creates an action object to get the number of displays a session has
     * @param {'onboarding'|'session'|null} [respondTo] - Name of the action array to put the response object
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    getDisplayCount: (respondTo = null) => {
        const returnObject = {
            action: 'getDisplayCount',
            executor: 'extension',
            type: 'action'
        };
        if (respondTo) {
            returnObject.respondTo = respondTo;
        }
        return  returnObject;
    },
    /**
     * Creates an action object to surface the "Share Screen" UI picker from chrome, and return the ID of the chosen screen
     * @param {'onboarding'|'session'|null} [respondTo] - Name of the action array to put the response object
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    getChromeDesktopId: (respondTo = null) => {
        const returnObject = {
            action: 'getChromeDesktopId',
            executor: 'extension',
            type: 'action'
        };
        if (respondTo) {
            returnObject.respondTo = respondTo;
        }
        return returnObject;
    },
    /**
     * Creates an action to hide the floating menu for an SP session WITHOUT ending the session. Be sure you know what you
     * are doing if you choose to use this action, as it could put the student in a stuck state.
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    hideFloatingMenu: () => {
        return {
            action: 'hideFloatingMenu',
            executor: 'extension',
            type: 'action'
        }
    },
    /**
     * Creates an action object to add all current chat messages into the requesting tab, or all tabs if one is not set
     * @param {object} chatMessages - Chat messages to add
     * @param {number | "*"} [tabId] - Integer tab ID for the requesting tab
     * @returns {{tabId, chatMessages, executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    initializeAllChatMessages: (chatMessages, tabId = "*") => {
        return {
            action: 'initializeAllChatMessages',
            tabId,
            chatMessages,
            executor: 'extension',
            type: 'action'
        }
    },
    /**
     * Creates an action to inject the password into all password fields in the current tab
     * @param {string} [passwordToInject] - Optional password to inject if different than what is in the sync doc
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    injectPassword: (passwordToInject = null) => {
        const returnObject = {
            action: 'injectPassword',
            executor: 'extension',
            type: 'action'
        };
        if (passwordToInject) {
            returnObject.syncDocField = passwordToInject;
        } else {
            // There is no password provided, so we must provide the one from the sync doc
            returnObject.requiredSyncDocField = 'sessionStart.decryptedPassword';
        }
        return returnObject;
    },
    /**
     * Creates an action to open the chrome settings for permissions
     * @param {string} [urlToOpenSiteSettings] - URL to open the chrome site settings for
     * @returns {{executor: string, action: string, type: string, [urlToOpenSiteSettings]: string}} - Object to send in `addAction()` or `addActions()`
     */
    openSettings: (urlToOpenSiteSettings = null) => {
        const returnObject = {
            action: 'openSettings',
            executor: 'extension',
            type: 'action'
        };
        if (urlToOpenSiteSettings) {
            returnObject.urlToOpenSiteSettings = urlToOpenSiteSettings;
        }
        return returnObject;
    },
    /**
     * Pins the Onboarding UI page and opens/pins the Session UI page
     * @param {string} examSid - Unique SID for Exams - EX12345678901234567890123456789012
     * @param {string} sessionSid - Unique SID for Sessions - ES12345678901234567890123456789012
     * @param {string} installSid - Unique SID for Installs - AI12345678901234567890123456789012
     * @param {string} token - Valid SP Token
     * @returns {{sessionSid, executor: string, installSid, action: string, examSid, type: string, token}} - Object to send in `addAction()` or `addActions()`
     */
    pinTabs: (examSid, sessionSid, installSid, token) => {
        return {
            action: 'pinTabs',
            executor: 'extension',
            type: 'action',
            examSid,
            sessionSid,
            installSid,
            token
        }
    },
    /**
     * Creates an action object to remove an array of modality options
     * @param {Object[]} modalityOptions - Array of ModalityOption objects
     * @returns {{modalityOptions, executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    removeModalityOptions: (modalityOptions) => {
        return {
            action: 'removeModalityOptions',
            modalityOptions,
            executor: 'extension',
            type: 'action'
        }
    },
    /**
     * Creates an action object to release all current modality options
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    releaseAllModalityOptions: () => {
        return {
            action: 'releaseAllModalityOptions',
            executor: 'extension',
            type: 'action'
        }
    },
    /**
     * Creates an action object to return focus to the exam tab
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    returnToExam: () => {
        return {
            action: 'returnToExam',
            executor: 'extension',
            type: 'action'
        }
    },
    /**
     * Creates an action to set the extension in progress to enable certain functionalities
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    setSessionInProgress: () => {
        return {
            action: 'setSessionInProgress',
            executor: 'extension',
            type: 'action'
        }
    },
    /**
     * Creates an action to show the floating menu for an SP session
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    showFloatingMenu: () => {
        return {
            action: 'showFloatingMenu',
            executor: 'extension',
            type: 'action'
        }
    }
}

ActionTypes.prototype.onboarding = {
    /**
     * Creates an action to alert the onboarding UI that the user shared their screen
     * @param {boolean} sharedScreen - Boolean that tells if the user canceled or shared their screen
     * @returns {{executor: string, action: string, sharedScreen: boolean, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    userSharedScreen: (sharedScreen = false) => {
        return {
            action: 'userSharedScreen',
            executor: 'onboarding',
            sharedScreen,
            type: 'action'
        }
    },

    /**
     * Creates an action to pass an error to the onboarding UI
     * @param {string} errorMessage - Error message to be sent to the Onboarding UI
     * @returns {{executor: string, action: string, errorMessage: string, type: string}}
     */
    passErrorToOnboarding: (errorMessage) => {
        return {
            action: 'passErrorToOnboarding',
            executor: 'onboarding',
            errorMessage,
            type: 'action'
        }
    }
}

ActionTypes.prototype.session = {
    /**
     * Creates an action object that starts up the recording for a session
     * @param {string} audioDeviceId - Unique local media ID for the device being used for audio
     * @param {string} videoDeviceId - Unique local media ID for the device being used for video
     * @param {string} screenDeviceId - ID returned in the callback from `chrome.desktopCapture.chooseDesktopMedia`
     * @param {'High'|'Mid'|'Low'} [resolutionBand] - Signifies what resolution to choose, based of user's internet upload speed
     * @returns {{executor: string, screenDeviceId, videoDeviceId, action: string, audioDeviceId, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    prepareAndStartRecording: (audioDeviceId, videoDeviceId, resolutionBand='High') => {
        return {
            action: 'prepareAndStartRecording',
            executor: 'session',
            type: 'action',
            audioDeviceId,
            videoDeviceId,
            resolutionBand
        }
    },
    createAnnotation: (annotationType) => {
        return {
            action: 'createAnnotation',
            executor: 'session',
            type: 'action',
            smarterProctoringAnnotationData: annotationType
        }
    },
    /**
     *
     * @param {Object} [filterOptions] - Object that can contain filtering options for the task - Options:
     * sessionSid:string,
     * installSid:string,
     * examSid:string,
     * courseSid:string,
     * doesAllowAnyProctor:boolean
     * @returns {{executor: string, action: string, type: string}}
     */
    createTaskForStudent: (filterOptions = {}) => {
        const defaultFilterOptions = {
            sessionSid: false,
            installSid: false,
            examSid: false,
            courseSid: false,
            doesAllowAnyProctor: false
        };
        const actualFilterOptions = Object.assign(defaultFilterOptions, filterOptions);
        return {
            action: 'createTaskForStudent',
            executor: 'session',
            type: 'action',
            sessionSid: actualFilterOptions.sessionSid,
            installSid: actualFilterOptions.installSid,
            examSid: actualFilterOptions.examSid,
            courseSid: actualFilterOptions.courseSid,
            doesAllowAnyProctor: actualFilterOptions.doesAllowAnyProctor
        }
    },
    /**
     * Creates an action object to start the utility app and begin the healthcheck
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()` or `addActions()`
     */
    startUtilityApp: () => {
        return {
            action: 'startUtilityApp',
            executor: 'session',
            type: 'action'
        }
    },
    /**
     * Creates an action object to start killing blocked apps on the utility app
     * @returns {{executor: string, action: string, type: string}} - Object to send in `addAction()`
     */
    startKillingBlockedApps: () => {
        return {
            action: 'startKillingBlockedApps',
            executor: 'session',
            type: 'action'
        }
    }
}

ActionTypes.prototype.response = {
    /**
     * Creates a response action object that returns a response to the provided action array from `respondTo`
     * @param {string} actionName - Name of the action that was taken and is creating a response for
     * @param {'extension'|'onboarding'|'session'} respondTo - Name of the array to put the response into
     * @param response - The result object from the action that was taken
     * @returns {{executor, response, action, type: string}} - Returns an action object that has a type of `response`
     */
    sendResponse: (actionName, respondTo, response) => {
        return {
            action: actionName,
            executor: respondTo,
            response: response,
            type: 'response'
        }
    }
}

module.exports = ActionTypes;